import React, { useEffect, useRef, useState } from 'react';

import { ComponentWrapper } from '../../../helperComponents/ComponentWrapper';
import { DropdownMenu } from '../../../helperComponents/DropdownMenu/DropdownMenu';
import { Alert, Platform, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { applyTransparency } from '../../../../constants/colors';
import { MenuButton } from '../../../helperComponents/MenuButton';
import {
  EAssessmentMode,
  EJourneyGameBadgeType,
  EStageId,
  EStageType,
  getAssessmentNameByStageId,
} from '../../../../utils/journeysUtils';
import { GAME_TYPES_TYPE } from '../../../../constants/constants';
import AssessmentTemplate from '../../../helperComponents/Assessment/AssessmentTemplate';
import { BackButtonV2 } from '../../../helperComponents/BackButtonV2';
import LabelCountBox from '../../../helperComponents/LabelCountBox';
import AssessmentResultPopUp from '../../../helperComponents/Journey/AssessmentResultPopUp';
import { useUpsertJourneyHistoryMutation } from '../../../../graphql/journey/useUpsertHistoryMutation.gql';
import { useUpdateJourneyStageMutation } from '../../../../graphql/journey/useUpdateJourneyMutation.gql';
import { useUpsertEarnedBadgeMutation } from '../../../../graphql/journey/useUpsertEarnedBadgeMutation.gql';
import { useSignedInContext } from '../../../../context/SignedInContext';
import { useGetJourneyGameBadgeByUserAndGameQuery } from '../../../../graphql/journey/getJourneyGameBadgesByUserAnGame.gql';
import { useUpsertJourneyGameBadgesMutation } from '../../../../graphql/journey/useUpsertJourneyGameBadgesMutation.gql';
import { useIncrementUserPointMutation } from '../../../../graphql/useIncrementUserPointMutation.gql';
import { useGetCurrentUserData } from '../../../../hooks/user';
import { useGetHivesQuery } from '../../MyHives/useGetHivesQuery.gql';

export default function JourneyAssessment({ navigation, route }) {
  const [assessmentMode, setAssessmentMode] = useState<EAssessmentMode | undefined>();
  const stageId = route.params?.stageId as EStageId;
  const gameType = route.params?.gameType as GAME_TYPES_TYPE;
  const journeyId = route.params?.journeyId as string;
  const nectarPoints = 5; // Points to award for completing the assessment
  const nectarPointsCompleteJourney = 10;

  const isMobile = Platform.OS !== 'web';
  const { user } = useSignedInContext();
  const userId = user?.id;

  const { width } = useWindowDimensions();
  const pathSizeLimit = width < 1161;
  const isMobileWeb = Platform.OS === 'web' && width <= 768;

  const [score, setScore] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);

  const [complete, setComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [upsertJourneyHistory, { loading }] = useUpsertJourneyHistoryMutation();
  const [updateJourneyStage] = useUpdateJourneyStageMutation();
  const [upsertJourneyGameBadges] = useUpsertJourneyGameBadgesMutation();
  const [upsertEarnedBadge] = useUpsertEarnedBadgeMutation();

  const [averageTimePerAnswer, setAverageTimePerAnswer] = useState(0);

  // 🕒 Timer state
  const [time, setTime] = useState(0); // in seconds
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [incrementUserPoint] = useIncrementUserPointMutation();
  const { refetchCurrentUserData } = useGetCurrentUserData();

  const questionsCount = 25;

  const finalStageAssessment =
    stageId === EStageId.Assessment3 && assessmentMode === EAssessmentMode.Game && userId;

  // Start timer only when Buzz Game starts
  useEffect(() => {
    if (assessmentMode === EAssessmentMode.Game && !complete) {
      // reset timer
      setTime(0);
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }

    // cleanup when leaving game or unmounting
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [assessmentMode, complete]);

  const { data: journeyGameBadgeData } = useGetJourneyGameBadgeByUserAndGameQuery({
    variables: {
      userId: userId || '',
      gameType,
    },
    fetchPolicy: 'network-only',
    skip: !userId,
  });

  // reset helper
  const resetGame = (mode: EAssessmentMode) => {
    setScore(0);
    setQuestionIndex(0);
    setFinalScore(0);
    setAssessmentMode(mode);
    setComplete(false);
  };

  const handleSubmit = async () => {
    try {
      await upsertJourneyHistory({
        variables: {
          journeyId,
          history: {
            stageId: stageId,
            type: EStageType.Assessment,
            completedAt: new Date().toISOString(),
            details: JSON.stringify({
              score: (finalScore / questionsCount) * 100,
              averageTimePerAnswer: averageTimePerAnswer,
            }),
          },
        },
      });

      await updateJourneyStage({
        variables: {
          id: journeyId,
          currentStage: stageId,
        },
      });

      console.log('Success', 'Assessment submitted successfully!');

      // award points

      const result = await incrementUserPoint({ variables: { points: nectarPoints } });

      if (!result.data?.incrementUserPoint) {
        throw new Error('Failed to increment user points');
      }

      refetchCurrentUserData();
      setShowCompleteModal(false);
      navigation.navigate('LobbyJourney', {
        gameType: gameType,
        journey: true,
      });
    } catch (err) {
      console.error(err);
      console.log('Error', 'Failed to submit Assessment.');
    }
  };

  const handleComplete = async ({ finalScore }: { finalScore: number }) => {
    setFinalScore(finalScore);
    setShowCompleteModal(true);
    setComplete(true);

    // 🛑 stop timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (assessmentMode === EAssessmentMode.Game) {
      const avgTime = time / questionsCount;
      setAverageTimePerAnswer(parseFloat(avgTime.toFixed(1)));
    }

    // ✅ Award "Complete" badge if Assessment3 is finished
    if (finalStageAssessment) {
      try {
        const existingDoc = journeyGameBadgeData?.journeyGameBadgeByUserAndGame;

        if (existingDoc?._id) {
          // 📝 Update existing doc with "Complete" badge
          await upsertEarnedBadge({
            variables: {
              id: existingDoc._id,
              badge: {
                type: EJourneyGameBadgeType.Complete,
                earnedAt: new Date().toISOString(),
              },
            },
          });
        } else {
          // 🆕 Create a new doc with "Complete" badge
          await upsertJourneyGameBadges({
            variables: {
              userId,
              gameType,
              data: {
                earnedBadges: [
                  {
                    type: EJourneyGameBadgeType.Complete,
                    earnedAt: new Date().toISOString(),
                  },
                ],
              },
            },
          });
        }

        console.log('🏅 Complete badge awarded for Assessment3');

        const result = await incrementUserPoint({
          variables: { points: nectarPointsCompleteJourney },
        });

        if (!result.data?.incrementUserPoint) {
          throw new Error('Failed to increment user points');
        }

        refetchCurrentUserData();
      } catch (err) {
        console.error('❌ Failed to award Complete badge:', err);
      }
    }
  };
  return (
    <ComponentWrapper
      backgroundColor="#FCEABB8C"
      hasBackground={false}
      hasInset
      scrollEnabled={false}
      style={{ paddingHorizontal: 0 }}
      hideBee={true}
    >
      <DropdownMenu
        backButton={
          <BackButtonV2
            onPress={() => {
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                navigation.navigate('LobbyJourney', {
                  gameType: gameType,
                  journey: true,
                });
              }
            }}
          />
        }
        top={20}
      />

      {assessmentMode && (
        <View
          style={[
            pathSizeLimit ? styles.gameStatusContainerSmall : styles.gameStatusContainer,
            isMobile && { marginTop: 80 },
            isMobileWeb && { marginTop: 130 },
          ]}
        >
          <View style={{ marginRight: pathSizeLimit ? 10 : 0 }}>
            <LabelCountBox label={'Score'} countText={`${score}/${questionIndex}`} />
          </View>
          <LabelCountBox
            label={'Questions Left'}
            countText={`${questionsCount - questionIndex}`}
            width={170}
          />
        </View>
      )}

      {!assessmentMode && (
        <>
          <Text style={[styles.title, (isMobile || isMobileWeb) && { marginTop: 150 }]}>
            {getAssessmentNameByStageId(stageId)}
          </Text>
          <MenuButton
            onPress={() => {
              setAssessmentMode(EAssessmentMode.Practice);
            }}
            colors={['#FEDD88', '#FFCF53']}
            text="Practice"
            width={302}
            height={65}
            style={{ marginTop: 46 }}
            textStyle={{
              fontSize: 21,
              fontFamily: 'Secular One',
              color: '#000000',
              fontWeight: '400',
            }}
          />
          <MenuButton
            onPress={() => {
              setAssessmentMode(EAssessmentMode.Game);
            }}
            colors={['#90DF75', '#62B655']}
            text="Start Buzz Game"
            width={302}
            height={65}
            style={{ marginTop: 18 }}
            textStyle={{
              fontSize: 21,
              fontFamily: 'Secular One',
              color: '#000000',
              fontWeight: '400',
            }}
          />
        </>
      )}
      {assessmentMode && !complete && (
        <AssessmentTemplate
          gameType={gameType}
          questionsCount={questionsCount}
          setQuestionIndex={(index: number) => setQuestionIndex(index)}
          setScore={(count: number) => setScore(count)}
          onComplete={handleComplete}
        />
      )}

      {showCompleteModal && !finalStageAssessment && (
        <AssessmentResultPopUp
          positive={true}
          title={'Done'}
          subtitle={'Great job!'}
          description={`You have completed the assessment ${
            assessmentMode === EAssessmentMode.Practice ? 'practice' : ''
          }!`}
          onClose={() => setShowCompleteModal(false)}
          visible={showCompleteModal}
          setVisible={setShowCompleteModal}
          practice={assessmentMode === EAssessmentMode.Practice}
          buttonText1={'Practice Again'}
          buttonText2={
            assessmentMode === EAssessmentMode.Practice
              ? 'Start Buzz Game'
              : 'Go to Learning Journey'
          }
          buttonFunction1={() => {
            setShowCompleteModal(false);
            resetGame(EAssessmentMode.Practice);
          }}
          buttonFunction2={
            assessmentMode === EAssessmentMode.Practice
              ? () => {
                  setShowCompleteModal(false);
                  resetGame(EAssessmentMode.Game);
                }
              : handleSubmit
          }
        />
      )}

      {showCompleteModal && finalStageAssessment && (
        <AssessmentResultPopUp
          positive={true}
          title={'Completed'}
          subtitle={'Congratulations!'}
          description={`You've completed the Learning Journey!`}
          onClose={() => setShowCompleteModal(false)}
          visible={showCompleteModal}
          setVisible={setShowCompleteModal}
          buttonText2={'Go to Lobby'}
          buttonFunction2={async () => {
            await handleSubmit();
            navigation.navigate('Lobby');
          }}
          showTrophy={true}
        />
      )}
    </ComponentWrapper>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    marginTop: 100,
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 28,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#E87030',
    marginTop: 100,
  },
  gameStatusContainer: {
    position: 'absolute',
    top: 20,
    right: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 20,
  },
  gameStatusContainerSmall: {
    marginTop: 30,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 20,
  },
});
